import * as ActionTypes from "constants/ActionTypes";
import cloneDeep from "lodash/cloneDeep";
import map from "lodash/map";
import filter from "lodash/filter";
import find from "lodash/find";
import get from "lodash/get";
import set from "lodash/set";
import flatten from "lodash/flatten";
import { normalizeCuepoint, normalizeHotspot } from "utils/normalizeStory";

const replaceHotspot = (storyState, hotspot) => {
  if (!hotspot.id) {
    console.warn("Unable to replace a hotspot without an id"); //eslint-disable-line no-console
    return storyState;
  }
  const storyCopy = cloneDeep(storyState);
  const hotspotCopy = cloneDeep(hotspot);

  let allHotspots = map(storyState.chapters, (chapterEntity) => ({
    chapterSchema: chapterEntity.schema,
    chapterId: chapterEntity.id,
    chapter: storyState.entities[chapterEntity.schema][chapterEntity.id],
  }));

  allHotspots = map(allHotspots, (chapter) =>
    map(chapter.chapter.hotspots, (hot) => ({
      hotspotId: hot.id,
      hotspotSchema: hot.schema,
      chapterSchema: chapter.chapterSchema,
      chapterId: chapter.chapterId,
    })),
  );
  allHotspots = flatten(allHotspots);

  hotspotCopy.localId = hotspotCopy.id;
  const normalized = normalizeHotspot(hotspotCopy);
  const newHotspotSchema = normalized.result.schema;
  const newHotspotId = normalized.result.id;
  const newHotspot = normalized.entities[newHotspotSchema][newHotspotId];

  if (!storyCopy.entities[newHotspotSchema]) {
    storyCopy.entities[newHotspotSchema] = {};
  }

  storyCopy.entities[newHotspotSchema][newHotspotId] = newHotspot;

  const oldHotspot = find(allHotspots, { hotspotId: hotspot.id });

  // Remove the hotspot from the old schema if it is different
  if (oldHotspot.hotspotSchema !== newHotspotSchema) {
    delete storyCopy.entities[oldHotspot.hotspotSchema][oldHotspot.hotspotId];
  }

  // Update the old chapter with the new hotspot info
  storyCopy.entities[oldHotspot.chapterSchema][oldHotspot.chapterId].hotspots =
    [
      ...filter(
        storyCopy.entities[oldHotspot.chapterSchema][oldHotspot.chapterId]
          .hotspots,
        (hot) => hot.id !== oldHotspot.hotspotId,
      ),
      {
        id: newHotspotId,
        schema: newHotspotSchema,
      },
    ];
  // end hotspot update

  // begin cuepoint update
  const normCuepoint = normalizeCuepoint(hotspotCopy);

  const newCuepointId = normCuepoint.result;
  const newTargetId = normCuepoint.entities.cuepoints[newCuepointId].target;
  const newThumbnailId =
    normCuepoint.entities.cuepoints[newCuepointId].thumbnail;

  storyCopy.entities.cuepoints[newCuepointId] =
    normCuepoint.entities.cuepoints[newCuepointId];
  storyCopy.entities.cuepointTargets[newTargetId] =
    normCuepoint.entities.cuepointTargets[newTargetId];
  storyCopy.entities.images[newThumbnailId] =
    normCuepoint.entities.images[newThumbnailId];

  return storyCopy;
};

const addHotspotToChapter = (storyState, hotspot, chapterId) => {
  if (!hotspot.id) {
    console.warn("Hotspot requires an 'id' property to add"); //eslint-disable-line no-console
    return storyState;
  }

  const storyClone = cloneDeep(storyState);
  const hotspotClone = cloneDeep(hotspot);
  const chapterEntity = find(storyClone.chapters, { id: chapterId });
  if (!chapterEntity) {
    throw new Error("Cannot add hotspot to a chapter that does not exist");
  }

  // normalize data
  hotspotClone.localId = hotspotClone.id;
  const normHotspot = normalizeHotspot(hotspotClone);
  const newHotspotSchema = normHotspot.result.schema;
  const newHotspotId = normHotspot.result.id;
  const normCuepoint = normalizeCuepoint(hotspotClone);

  // Check entities for undefined collections
  ["cuepoints", "cuepointTargets", "images", newHotspotSchema].forEach(
    (key) => {
      if (!get(storyClone.entities, key)) {
        set(storyClone.entities, key, {});
      }
    },
  );

  //Check entities for undefined arrays
  [
    `[${chapterEntity.schema}][${chapterEntity.id}].cuepoints`,
    `[${chapterEntity.schema}][${chapterEntity.id}].hotspots`,
  ].forEach((key) => {
    if (!get(storyClone.entities, key)) {
      set(storyClone.entities, key, []);
    }
  });

  // add hotspot to given schema
  storyClone.entities[newHotspotSchema][newHotspotId] =
    normHotspot.entities[newHotspotSchema][newHotspotId];

  const newCuepointId = normCuepoint.result;
  const newTargetId = normCuepoint.entities.cuepoints[newCuepointId].target;
  const newThumbnailId =
    normCuepoint.entities.cuepoints[newCuepointId].thumbnail;

  // Add cuepoints to given entities.
  storyClone.entities.cuepoints[newCuepointId] =
    normCuepoint.entities.cuepoints[newCuepointId];
  storyClone.entities.cuepointTargets[newTargetId] =
    normCuepoint.entities.cuepointTargets[newTargetId];
  storyClone.entities.images[newThumbnailId] =
    normCuepoint.entities.images[newThumbnailId];

  // Add hotspot to chapter
  storyClone.entities[chapterEntity.schema][chapterEntity.id].hotspots.push({
    schema: newHotspotSchema,
    id: newHotspotId,
  });

  // Add cuepoint to chapter
  storyClone.entities[chapterEntity.schema][chapterEntity.id].cuepoints.push(
    newCuepointId,
  );

  return storyClone;
};

function story(state = null, action) {
  switch (action.type) {
    case ActionTypes.STORY_LOADED:
      return action.story;
    case ActionTypes.UPDATE_HOTSPOT:
      if (!state)
        throw new Error("Cannot call story reducer with empty `state`");
      return replaceHotspot(state, action.hotspot);
    case ActionTypes.ADD_HOTSPOT:
      return addHotspotToChapter(state, action.hotspot, action.chapterId);
    default:
      return state;
  }
}

// We don't use `combineReducers` because this is the only one
export default story;
